import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ChevronDown, Paperclip, FileText, Users, UserPlus, X, Link2, AlertCircle } from "lucide-react";
import { getQualityMetaList, getQualityMeta, createQualityMeta, updateQualityMeta } from "../../../api/quality/meta";
import MobileAttachmentBox from "../../../components/common/papers/MobileAttachmentBox";
import MobileReferenceModal from "../../../components/approval/MobileReferenceModal";
import { getDocPersons } from "../../../api/papers";

// meta.form 혹은 평면 meta 대응
const pickFormFromMeta = (meta) => {
  if (!meta || typeof meta !== "object") return {};
  return meta.form || meta;
};

// NCR 상세 메타
const pickNcrMeta = (meta) => {
  if (!meta || typeof meta !== "object") return {};
  return meta.ncrMeta || {};
};

export default function CarNew() {
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [dns, setDns] = useState(doc_dns || "");
  const [saving, setSaving] = useState(false);
  const attachmentRef = useRef(null);

  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";

  const [linkedNcr, setLinkedNcr] = useState(null);
  const [ncrList, setNcrList] = useState([]);
  
  // 조치 담당자
  const [requestPeople, setRequestPeople] = useState([]);
  const [peopleModalOpen, setPeopleModalOpen] = useState(false);
  const [tempSelectedIds, setTempSelectedIds] = useState([]);

  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    title: "",
    note: "",
  });

  const [carMeta, setCarMeta] = useState({
    ncr_dns: "",
    responsible: "",
    due_date: "",
    close_date: "",
    root_cause: "",
    corrective_action: "",
    preventive_action: "",
    verification: "",
    severity: "",
  });

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    linkedNcr: true,
    docInfo: true,
    carInfo: true,
    people: true,
    actions: true,
    attachment: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onChangeMeta = (k, v) => setCarMeta((p) => ({ ...p, [k]: v }));

  // 선택된 NCR 기반 자동 제목
  const autoTitle = useMemo(() => {
    const meta = linkedNcr?.meta || {};
    const f = pickFormFromMeta(meta);

    const base = linkedNcr?.doc?.title?.trim() || f.title?.trim();
    if (base) return `[CAR] ${base}`;

    if (carMeta.ncr_dns) return `[CAR] ${carMeta.ncr_dns} 시정조치`;

    return "";
  }, [linkedNcr, carMeta.ncr_dns]);

  // NCR 한 건 로드
  const loadNcr = useCallback(async (ncrDns) => {
    if (!ncrDns) return;
    try {
      const res = await getQualityMeta({ dns: ncrDns });
      const ncr = res?.data || res;

      setLinkedNcr(ncr);

      const meta = ncr?.meta || {};
      const f = pickFormFromMeta(meta);
      const nm = pickNcrMeta(meta);

      setCarMeta((prev) => ({
        ...prev,
        ncr_dns: ncr.doc_dns || ncrDns,
        responsible: prev.responsible || nm.action_dept || "",
        due_date: prev.due_date || (nm.due_type === "date" ? nm.due_date : "") || "",
      }));

      setForm((prev) => ({
        ...prev,
        title: prev.title?.trim()
          ? prev.title
          : ncr.doc?.title
            ? `[CAR] ${ncr.doc.title}`
            : f.title
              ? `[CAR] ${f.title}`
              : prev.title,
      }));
    } catch (err) {
      console.error("NCR 불러오기 실패:", err);
    }
  }, []);

  // URL ?fromNCR=... 있으면 자동 연결
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ncrDns = params.get("fromNCR");
    if (ncrDns) {
      loadNcr(ncrDns);
    }
  }, [location.search, loadNcr]);

  // 프로젝트 NCR 목록
  useEffect(() => {
    const fetchNcrList = async () => {
      try {
        const res = await getQualityMetaList({ project: projectId });
        const rows = Array.isArray(res) ? res : res?.results || [];

        const onlyNcr = rows.filter((r) => {
          const meta = r.meta || {};
          const ft = String(meta.__client__?.form_type || meta.form_type || "").toUpperCase();
          return ft === "NC" || ft === "NCR";
        });

        setNcrList(onlyNcr);
      } catch (err) {
        console.error("NCR 목록 불러오기 실패:", err);
      }
    };

    if (projectId) fetchNcrList();
  }, [projectId]);

  // 기존 문서 로드
  useEffect(() => {
    if (!doc_dns) return;

    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns: doc_dns });
        const meta = res?.meta || {};

        if (meta.form) {
          setForm((prev) => ({ ...prev, ...meta.form }));
        }

        if (meta.carMeta) {
          setCarMeta((prev) => ({ ...prev, ...meta.carMeta }));
        }

        const linked = meta.carMeta?.ncr_dns;
        if (linked) {
          loadNcr(linked);
        }

        // 담당자 로드
        try {
          const people = await getDocPersons(doc_dns);
          const mapped = people.map((p) => ({
            id: p.user.id,
            name: p.user.name || `${p.user.last_name ?? ""}${p.user.first_name ?? ""}`,
          }));
          setRequestPeople(mapped);
        } catch (e) {
          console.error("담당자 로드 실패:", e);
        }
      } catch (err) {
        console.error("CAR 수정모드 meta 로딩 실패:", err);
      }
    }

    loadDetail();
  }, [doc_dns, loadNcr]);

  // 담당자 모달
  const openPeopleModal = () => {
    setTempSelectedIds(requestPeople.map((p) => p.id));
    setPeopleModalOpen(true);
  };

  const handlePeopleSave = (ids, userDetails) => {
    const mapped = userDetails.map((u) => ({
      id: u.id,
      name: u.name ?? `${u.last_name ?? ""}${u.first_name ?? ""}`,
    }));
    setRequestPeople(mapped);
    setPeopleModalOpen(false);
    return true;
  };

  const removePerson = (id) => {
    setRequestPeople((prev) => prev.filter((p) => p.id !== id));
  };

  // 저장 처리
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const body = {
        layout: null,
        values: {
          project: projectId,
          module: "quality",
          doc_type: "CA",
          dns,
          meta: {
            form: {
              ...form,
              title: form.title?.trim() || autoTitle,
            },
            carMeta,
            __client__: { form_type: "CA" },
            related_dns: linkedNcr?.doc_dns || carMeta.ncr_dns || null,
          },
        },
      };

      let result;
      if (dns) {
        result = await updateQualityMeta(dns, body.values);
      } else {
        result = await createQualityMeta(body.values);
      }

      const newDns = result?.dns || result?.doc_dns || dns;

      if (newDns && attachmentRef.current) {
        await attachmentRef.current.save(newDns);
      }

      if (newDns) {
        setDns(newDns);
        alert("저장 완료!");
        navigate(`/project/${projectId}/quality/car/${encodeURIComponent(newDns)}/view`);
      } else {
        alert("저장 완료!");
        navigate(BOARD);
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const BOARD = `/project/${projectId}/quality/list/all`;
  const handleBack = () => navigate(BOARD);

  // NCR 요약 데이터
  const ncrMeta = linkedNcr?.meta || {};
  const ncrForm = pickFormFromMeta(ncrMeta);
  const ncrDetail = pickNcrMeta(ncrMeta);

  // 상태 텍스트
  const getStatusText = (status) => {
    const map = {
      draft: "작성중",
      in_progress: "조치중",
      pending_verify: "검증대기",
      closed: "종결",
    };
    return map[status] || status;
  };

  // 위험도 색상
  const getSeverityColor = (severity) => {
    const map = {
      High: "bg-red-100 text-red-700",
      Medium: "bg-yellow-100 text-yellow-700",
      Low: "bg-green-100 text-green-700",
    };
    return map[severity] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="px-4 pt-4 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">품질</p>
          <h2 className="text-xl font-bold text-gray-900">시정조치서 (CAR)</h2>
        </header>

        {/* 요약 카드 */}
        <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-blue-600" />
              <span className="text-xs font-medium text-blue-800">시정조치</span>
            </div>
            <div className="flex items-center gap-2">
              {carMeta.severity && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(carMeta.severity)}`}>
                  {carMeta.severity}
                </span>
              )}
              <span className="text-xs text-blue-600">{form.doc_date}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {getStatusText(form.status)}
              </div>
              <div className="text-[10px] text-gray-500">상태</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {carMeta.due_date || "-"}
              </div>
              <div className="text-[10px] text-gray-500">조치기한</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {linkedNcr ? "연결됨" : "미연결"}
              </div>
              <div className="text-[10px] text-gray-500">NCR</div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* 1. 연결된 NCR */}
          <Section 
            title="연결된 NCR" 
            expanded={expandedSections.linkedNcr} 
            onToggle={() => toggleSection("linkedNcr")}
            icon={<Link2 size={16} className="text-gray-500" />}
          >
            {linkedNcr ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{linkedNcr.doc_dns}</p>
                      <p className="text-xs text-gray-500">{ncrForm.title || "제목 없음"}</p>
                    </div>
                  </div>
                </div>
                
                {/* NCR 요약 정보 */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="px-3 py-2 rounded-lg bg-gray-50">
                    <span className="text-gray-500">발생장소</span>
                    <p className="font-medium text-gray-800">{ncrDetail.location || "-"}</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-gray-50">
                    <span className="text-gray-500">부적합형태</span>
                    <p className="font-medium text-gray-800">{ncrDetail.nc_type || "-"}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-400 text-center py-2 mb-3">연결된 NCR이 없습니다</p>
                <div>
                  <label className="text-gray-500 text-xs mb-1 block">NCR 선택</label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
                    onChange={(e) => e.target.value && loadNcr(e.target.value)}
                    defaultValue=""
                  >
                    <option value="" disabled>NCR을 선택하세요</option>
                    {ncrList.map((ncr) => {
                      const m = ncr.meta || {};
                      const f = pickFormFromMeta(m);
                      return (
                        <option key={ncr.doc_dns} value={ncr.doc_dns}>
                          {ncr.doc_dns} - {f.title || ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}
          </Section>

          {/* 2. 문서 정보 */}
          <Section title="문서 정보" expanded={expandedSections.docInfo} onToggle={() => toggleSection("docInfo")}>
            <InputField
              label="제목"
              value={form.title}
              onChange={(v) => onChange("title", v)}
              placeholder={autoTitle || "문서 제목"}
            />
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="문서일자"
                type="date"
                value={form.doc_date}
                onChange={(v) => onChange("doc_date", v)}
              />
              <div>
                <label className="text-gray-500 text-xs mb-1 block">상태</label>
                <select
                  value={form.status}
                  onChange={(e) => onChange("status", e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
                >
                  <option value="draft">작성중</option>
                  <option value="in_progress">조치중</option>
                  <option value="pending_verify">검증대기</option>
                  <option value="closed">종결</option>
                </select>
              </div>
            </div>
          </Section>

          {/* 3. CAR 정보 */}
          <Section title="CAR 정보" expanded={expandedSections.carInfo} onToggle={() => toggleSection("carInfo")}>
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="조치기한"
                type="date"
                value={carMeta.due_date}
                onChange={(v) => onChangeMeta("due_date", v)}
              />
              <InputField
                label="종결일"
                type="date"
                value={carMeta.close_date}
                onChange={(v) => onChangeMeta("close_date", v)}
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">위험도</label>
              <div className="grid grid-cols-3 gap-2">
                {["High", "Medium", "Low"].map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => onChangeMeta("severity", level)}
                    className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      carMeta.severity === level
                        ? getSeverityColor(level)
                        : "bg-white border border-gray-200 text-gray-600"
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* 4. 조치 담당자 */}
          <Section 
            title="조치 담당자" 
            expanded={expandedSections.people} 
            onToggle={() => toggleSection("people")}
            icon={<Users size={16} className="text-gray-500" />}
            badge={requestPeople.length > 0 ? `${requestPeople.length}명` : null}
          >
            {requestPeople.length > 0 ? (
              <div className="space-y-2">
                {requestPeople.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-100"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {(person.name || "?").charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-800">{person.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePerson(person.id)}
                      className="p-1.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">선택된 담당자가 없습니다</p>
            )}

            <button
              type="button"
              onClick={openPeopleModal}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <UserPlus size={18} />
              <span className="text-sm font-medium">담당자 선택</span>
            </button>

            <MobileReferenceModal
              isOpen={peopleModalOpen}
              onClose={() => setPeopleModalOpen(false)}
              selectedIds={tempSelectedIds}
              name="조치 담당자"
              onChange={(ids) => setTempSelectedIds(ids)}
              onSave={handlePeopleSave}
            />
          </Section>

          {/* 5. 조치 내용 */}
          <Section title="조치 내용" expanded={expandedSections.actions} onToggle={() => toggleSection("actions")}>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">근본원인 (Root Cause)</label>
              <textarea
                value={carMeta.root_cause}
                onChange={(e) => onChangeMeta("root_cause", e.target.value)}
                placeholder="부적합의 근본적인 원인을 분석하여 작성"
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">시정조치 (Corrective Action)</label>
              <textarea
                value={carMeta.corrective_action}
                onChange={(e) => onChangeMeta("corrective_action", e.target.value)}
                placeholder="발생한 문제를 해결하기 위한 조치 내용"
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">예방조치 (Preventive Action)</label>
              <textarea
                value={carMeta.preventive_action}
                onChange={(e) => onChangeMeta("preventive_action", e.target.value)}
                placeholder="재발 방지를 위한 예방 조치 내용"
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">검증 (Verification)</label>
              <textarea
                value={carMeta.verification}
                onChange={(e) => onChangeMeta("verification", e.target.value)}
                placeholder="조치 결과 검증 방법 및 기준"
                rows={2}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">비고</label>
              <textarea
                value={form.note}
                onChange={(e) => onChange("note", e.target.value)}
                placeholder="추가 메모"
                rows={2}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
          </Section>

          {/* 6. 첨부자료 */}
          <Section 
            title="첨부자료" 
            expanded={expandedSections.attachment} 
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentRef} />
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            onClick={handleBack}
          >
            목록
          </button>
          <button
            type="button"
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="p-4 space-y-3">{children}</div>
      )}
    </section>
  );
}

// 입력 필드 컴포넌트
function InputField({ label, value, onChange, type = "text", placeholder, disabled, required }) {
  return (
    <div>
      <label className="text-gray-500 text-xs mb-1 block">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  );
}
